import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../services/api';

const KasaContext = createContext(null);

function parseISOLokal(iso) {
  if (!iso) return null;
  if (iso.includes('Z') || iso.includes('+') || iso.match(/\d{2}:\d{2}:\d{2}-\d{2}/)) {
    return new Date(iso);
  }
  const [datePart, timePart = '00:00:00'] = iso.split('T');
  const [y,m,d] = datePart.split('-').map(Number);
  const [h,mi,s] = timePart.split(':').map(Number);
  return new Date(y, m-1, d, h, mi, s || 0);
}

function fmtTarih(d) {
  if (!d) return '';
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
}
function fmtSaat(d) {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function mapGelir(g) {
  const tarih = parseISOLokal(g.tarih);
  const tarihStr = fmtTarih(tarih);
  const saatStr  = fmtSaat(tarih);
  return {
    id: g.kasaGelirId, kasaGelirId: g.kasaGelirId,
    kaynak: 'kasa',
    tarih: tarihStr, saat: saatStr, tarihSaat: `${tarihStr} ${saatStr}`,
    ad: g.aciklama || '', grup: g.gelirGrubuAd || '', gelirGrubuId: g.gelirGrubuId,
    odemeTip: g.odemeTipi || '', banka: g.bankaAdi || '',
    tutar: g.tutar || 0, paraBirimi: g.paraBirimi || 'TL',
    kurDegeri: g.kurDegeri || 1,
    personel: g.personelAd || '', personelId: g.personelId,
  };
}

function mapGider(g) {
  const tarih = parseISOLokal(g.tarih);
  const tarihStr = fmtTarih(tarih);
  const saatStr  = fmtSaat(tarih);
  return {
    id: g.kasaGiderId, kasaGiderId: g.kasaGiderId,
    altTip: g.giderTipi || 'gider',
    tarih: tarihStr, saat: saatStr, tarihSaat: `${tarihStr} ${saatStr}`,
    ad: g.aciklama || '', grup: g.giderGrubuAd || g.personelHedefAd || '',
    giderGrubuId: g.giderGrubuId, personelHedefId: g.personelHedefId,
    odemeTip: g.odemeTipi || '', banka: g.bankaAdi || '',
    tutar: g.birimTutar || 0, miktar: g.miktar || 1, toplam: g.toplamTutar || 0,
    paraBirimi: g.paraBirimi || 'TL',
    kurDegeri: g.kurDegeri || 1,
    personel: g.personelAd || '', personelId: g.personelId,
  };
}

function mapOdeme(o) {
  const tarih = parseISOLokal(o.odemeTarihi);
  const tarihStr = fmtTarih(tarih);
  const saatStr  = o.odemeSaati || fmtSaat(tarih);
  return {
    id: `odeme-${o.odemeId}`, odemeId: o.odemeId,
    kaynak: o.rezervasyonId ? 'rezervasyon' : (o.aciklama || '').startsWith('Havuz girişi') ? 'havuz' : 'walk-in',
    isAdisyonOdemesi: (o.aciklama || '').startsWith('Adisyon ödemesi'),
    tarih: tarihStr, saat: saatStr, tarihSaat: `${tarihStr} ${saatStr}`,
    ad: o.aciklama || 'Rez. ödemesi',
    grup: !o.rezervasyonId ? ((o.aciklama || '').startsWith('Havuz girişi') ? 'HAVUZ' : 'WALK-IN') : (o.aciklama || '').startsWith('Adisyon ödemesi') ? 'ADİSYON ÖDEMESİ' : 'KONAKLAMA',
    rezervasyonId: o.rezervasyonId,
    odemeTip: o.odemeTipi || '', banka: o.bankaAdi || '',
    tutar: o.tutar || 0, paraBirimi: o.paraBirimi || 'TL', kurDegeri: o.kurDegeri || 1,
    personel: o.personelAd || '', personelId: o.personelId,
  };
}

function mapAdisyon(a) {
  // Adisyon entity'de KayitTarihi var, DTO'da OlusturmaTarihi olarak geliyor
  const tarih = parseISOLokal(a.olusturmaTarihi || a.kayitTarihi);
  const tarihStr = fmtTarih(tarih);
  return {
    id: a.adisyonId,
    adisyonId: a.adisyonId,
    rezervasyonId: a.rezervasyonId,
    stokAdi: a.stokAdi || '',
    stokId: a.stokId,
    miktar: a.miktar || 1,
    birim: a.birim || 'Adet',
    birimFiyat: a.birimFiyat || 0,
    iskonto: a.iskonto || 0,
    toplamTutar: a.toplamTutar || 0,
    odemeyapildiMi: a.odemeyapildiMi || false,
    odenenTutar: a.odenenTutar || 0,
    tarih: tarihStr,
    personelAd: a.personelAd || '',
  };
}

export function KasaProvider({ children }) {
  const [kasaGelirler,   setKasaGelirler]   = useState([]);
  const [odemeler,       setOdemeler]       = useState([]);
  const [giderler,       setGiderler]       = useState([]);
  const [adisyonlar,     setAdisyonlar]     = useState([]);
  const [gelirGruplari,  setGelirGruplari]  = useState([]);
  const [giderGruplari,  setGiderGruplari]  = useState([]);
  const [personeller,    setPersoneller]    = useState([]);
  const [odemeTipleri,   setOdemeTipleri]   = useState([]);
  const [bankalar,       setBankalar]       = useState([]);
  const [paraBirimleri,  setParaBirimleri]  = useState([]);
  const [dovizKurlari,   setDovizKurlari]   = useState({ TRY: 1, TL: 1, EUR: 1, USD: 1, GBP: 1, CHF: 1, SAR: 1, AED: 1, RUB: 1, JPY: 1 });
  const [yukleniyor,     setYukleniyor]     = useState(true);

  const gelirler = [...kasaGelirler, ...odemeler].sort((a,b) => {
    const da = a.tarih?.split('.').reverse().join('') + a.saat;
    const db = b.tarih?.split('.').reverse().join('') + b.saat;
    return db.localeCompare(da);
  });

  const fetchVeriler = useCallback(async () => {
    setYukleniyor(true);
    const results = await Promise.allSettled([
      apiRequest('kasa-gelir'),
      apiRequest('kasa-gider'),
      apiRequest('odemeler'),
      apiRequest('adisyonlar'),
      apiRequest('gelirgruplari'),
      apiRequest('gidergruplari'),
      apiRequest('personeller'),
      apiRequest('odemetipleri'),
      apiRequest('bankatanimlari'),
      apiRequest('ParaBirimi'),
    ]);
    const val = (i) => results[i].status === 'fulfilled' ? results[i].value : null;
    const [gelirData, giderData, odemeData, adisyonData, gelirGrupData, giderGrupData,
           personelData, odemeTipData, bankaData, paraBirimiData] =
      [val(0), val(1), val(2), val(3), val(4), val(5), val(6), val(7), val(8), val(9)];
    setKasaGelirler((gelirData    || []).map(mapGelir));
    setGiderler(    (giderData    || []).map(mapGider));
    setOdemeler(    (odemeData    || []).map(mapOdeme));
    setAdisyonlar(  (adisyonData  || []).map(mapAdisyon));
    setGelirGruplari((gelirGrupData || []).filter(g => g.aktifMi !== false));
    setGiderGruplari((giderGrupData || []).filter(g => g.aktifMi !== false));
    setPersoneller(  (personelData  || []).filter(p => p.aktifMi !== false));
    setOdemeTipleri( (odemeTipData  || []).filter(o => o.aktifMi !== false));
    setBankalar(     (bankaData     || []).filter(b => b.aktifMi !== false));
    setParaBirimleri((paraBirimiData || []).filter(p => p.aktifMi !== false));
    setYukleniyor(false);
  }, []);

  useEffect(() => { fetchVeriler(); }, [fetchVeriler]);

  // Döviz kurlarını çek (open.er-api.com — ücretsiz, CORS açık)
  useEffect(() => {
    async function kurCek() {
      try {
        const res  = await fetch('https://open.er-api.com/v6/latest/TRY');
        const data = await res.json();
        if (data.result === 'success') {
          const r = data.rates;
          const yeniKurlar = { TRY: 1, TL: 1 };
          Object.keys(r).forEach(kod => {
            if (r[kod] > 0) yeniKurlar[kod] = 1 / r[kod];
          });
          setDovizKurlari(yeniKurlar);
        }
      } catch (err) {
        console.warn('Döviz kuru alınamadı, kurlar 1:1 kullanılacak:', err);
      }
    }
    kurCek();
  }, []);

  async function gelirEkle(kayit) {
    const [g,a,y] = (kayit.tarih || '').split('.').map(Number);
    const [sa,dk] = (kayit.saat || '00:00').split(':').map(Number);
    const tarihISO = `${y}-${String(a).padStart(2,'0')}-${String(g).padStart(2,'0')}T${String(sa).padStart(2,'0')}:${String(dk).padStart(2,'0')}:00`;
    const dto = { aciklama: kayit.ad, tutar: parseFloat(kayit.tutar)||0, paraBirimi: kayit.paraBirimi||'TL', kurDegeri: parseFloat(kayit.kurDegeri)||1, odemeTipi: kayit.odemeTip, bankaAdi: kayit.banka||'', tarih: tarihISO, gelirGrubuId: kayit.gelirGrubuId||0, personelId: kayit.personelId||null };
    const yeni = await apiRequest('kasa-gelir', 'POST', dto);
    if (yeni) setKasaGelirler(prev => [mapGelir(yeni), ...prev]);
  }

  async function gelirGuncelle(kayit) {
    const [g,a,y] = (kayit.tarih || '').split('.').map(Number);
    const [sa,dk] = (kayit.saat || '00:00').split(':').map(Number);
    const tarihISO = `${y}-${String(a).padStart(2,'0')}-${String(g).padStart(2,'0')}T${String(sa).padStart(2,'0')}:${String(dk).padStart(2,'0')}:00`;
    const dto = { kasaGelirId: kayit.id||kayit.kasaGelirId, aciklama: kayit.ad, tutar: parseFloat(kayit.tutar)||0, paraBirimi: kayit.paraBirimi||'TL', kurDegeri: parseFloat(kayit.kurDegeri)||1, odemeTipi: kayit.odemeTip, bankaAdi: kayit.banka||'', tarih: tarihISO, gelirGrubuId: kayit.gelirGrubuId||0, personelId: kayit.personelId||null };
    await apiRequest('kasa-gelir', 'PUT', dto);
    await fetchVeriler();
  }

  async function gelirSil(id) {
    await apiRequest(`kasa-gelir/${id}`, 'DELETE');
    setKasaGelirler(prev => prev.filter(g => String(g.id) !== String(id)));
  }

  async function odemeSil(id) {
    await apiRequest(`odemeler/${id}`, 'DELETE');
    setOdemeler(prev => prev.filter(g => String(g.id) !== String(id)));
  }

  async function giderEkle(kayit) {
    const [g,a,y] = (kayit.tarih || '').split('.').map(Number);
    const [sa,dk] = (kayit.saat || '00:00').split(':').map(Number);
    const tarihISO = `${y}-${String(a).padStart(2,'0')}-${String(g).padStart(2,'0')}T${String(sa).padStart(2,'0')}:${String(dk).padStart(2,'0')}:00`;
    const dto = { giderTipi: kayit.altTip||'gider', aciklama: kayit.ad, birimTutar: parseFloat(kayit.tutar)||0, miktar: parseInt(kayit.miktar)||1, paraBirimi: kayit.paraBirimi||'TL', kurDegeri: parseFloat(kayit.kurDegeri)||1, odemeTipi: kayit.odemeTip, bankaAdi: kayit.banka||'', tarih: tarihISO, giderGrubuId: kayit.giderGrubuId||null, personelHedefId: kayit.personelHedefId||null, personelId: kayit.personelId||null };
    const yeni = await apiRequest('kasa-gider', 'POST', dto);
    if (yeni) setGiderler(prev => [mapGider(yeni), ...prev]);
  }

  async function giderGuncelle(kayit) {
    const [g,a,y] = (kayit.tarih || '').split('.').map(Number);
    const [sa,dk] = (kayit.saat || '00:00').split(':').map(Number);
    const tarihISO = `${y}-${String(a).padStart(2,'0')}-${String(g).padStart(2,'0')}T${String(sa).padStart(2,'0')}:${String(dk).padStart(2,'0')}:00`;
    const dto = { kasaGiderId: kayit.id||kayit.kasaGiderId, giderTipi: kayit.altTip||'gider', aciklama: kayit.ad, birimTutar: parseFloat(kayit.tutar)||0, miktar: parseInt(kayit.miktar)||1, paraBirimi: kayit.paraBirimi||'TL', kurDegeri: parseFloat(kayit.kurDegeri)||1, odemeTipi: kayit.odemeTip, bankaAdi: kayit.banka||'', tarih: tarihISO, giderGrubuId: kayit.giderGrubuId||null, personelHedefId: kayit.personelHedefId||null, personelId: kayit.personelId||null };
    await apiRequest('kasa-gider', 'PUT', dto);
    await fetchVeriler();
  }

  async function giderSil(id) {
    await apiRequest(`kasa-gider/${id}`, 'DELETE');
    setGiderler(prev => prev.filter(g => String(g.id) !== String(id)));
  }

  return (
    <KasaContext.Provider value={{
      gelirler, giderler, adisyonlar,
      gelirGruplari, giderGruplari, personeller,
      odemeTipleri, bankalar, paraBirimleri, dovizKurlari,
      yukleniyor, fetchVeriler,
      gelirEkle, gelirGuncelle, gelirSil, odemeSil,
      giderEkle, giderGuncelle, giderSil,
    }}>
      {children}
    </KasaContext.Provider>
  );
}

export function useKasa() {
  return useContext(KasaContext);
}